import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { logError } from '../utils/errorHandler';
import { useAuth } from './AuthContext';

const AdminContext = createContext();

export const AdminProvider = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminProfile, setAdminProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    if (!currentUser) {
      setIsAdmin(false);
      setAdminProfile(null);
      setLoading(false);
      return;
    }

    const adminRef = doc(db, 'adminUsers', currentUser.uid);
    const unsubscribe = onSnapshot(adminRef, (snapshot) => {
      const exists = snapshot.exists();
      setIsAdmin(exists);
      setAdminProfile(exists ? snapshot.data() : null);
      setLoading(false);
    }, (error) => {
      logError(error, { action: 'adminSnapshot', userId: currentUser.uid });
      setIsAdmin(false);
      setAdminProfile(null);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser, authLoading]);

  const value = useMemo(() => ({
    isAdmin,
    adminProfile,
    loading
  }), [isAdmin, adminProfile, loading]);

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};
